import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSurfaceLayer } from '../lib/useDismiss'

/**
 * The app's first real modal dialog. REC-20.
 *
 * ── WHY THIS IS A COMPONENT AND NOT `useDismiss` WITH A SCRIM ───────────────
 *
 * `useDismiss` says in its own header that it does NOT trap focus and must not,
 * because a trap belongs to a dialog and its callers are menus. That was right,
 * and it is exactly why a dialog cannot be built out of it. A modal has to do
 * four things a menu must never do: trap Tab, tell assistive technology the rest
 * of the page is unavailable, stop the page behind it scrolling, and put focus
 * back where it came from on the way out.
 *
 * The bell panel is the cautionary tale. It shipped `role="dialog"` while doing
 * none of those, and 046 called that worse than no role at all and downgraded it
 * to a labelled group. So this component exists to be the only thing in the hub
 * that makes the promise — and the bell is deliberately NOT retrofitted into it,
 * because a dropdown is still not a dialog.
 *
 * ── THE DESIGN SYSTEM'S Dialog WAS READ AND NOT USED ────────────────────────
 *
 * `components/feedback/Dialog.jsx` has the same zero-keyboard-handling gap every
 * other component in that package had on 10 Sep: no focus trap, no Escape, no
 * focus return, no scroll lock. It is also `position: absolute` rather than
 * fixed, and hard-codes `zIndex: 50` where the app now has `--z-dialog`. It
 * announces `aria-modal="true"` regardless. Read it, do not assume it works —
 * REC-20 said so and it was accurate.
 *
 * ── A PORTAL IN THE BROWSER, INLINE UNDER renderToString (29 Sep) ─────────
 *
 * This used to render in place, on the claim that nothing in the layout made a
 * fixed element misbehave. The claim was about CONTAINING BLOCKS and it was
 * true; it missed STACKING CONTEXTS. `.hub__side` is `position: sticky`, and a
 * sticky element always forms a stacking context — so a dialog opened from the
 * rail ("See your stars") had its z-index of 50 counted INSIDE the rail, and
 * the hubbar and the dev banner (sticky, z 5, outside it) painted over the top
 * of the panel. That is David's 26 Sep ticket word for word: the top of the
 * modal under the banner, no × to press, no way out. Measured in a browser
 * before the fix: the scrim's parent was ASIDE.hub__side and the hubbar
 * covered the panel's first 20px.
 *
 * So in a browser the dialog is portalled to <body>, where no ancestor can
 * form a context around it, wherever its trigger sits. Under renderToString
 * there is no document, `createPortal` would throw, and the suite is the only
 * thing that mounts this component — so there it renders in place, exactly as
 * before. React events still bubble through the React tree from a portal,
 * which is what keeps onClose, the Tab trap and the stack working unchanged.
 *
 * ── THE SCRIM DISMISSES UNLESS SOMETHING HAS BEEN TYPED (David, 29 Sep) ────
 *
 * REC-20 shipped a scrim that never dismissed, on the reasoning that a stray
 * click would destroy a half-written message with no undo. The reasoning was
 * right about that one case and wrong as a rule: the welcome modal, the shop
 * item, the earnings panel and the draft picker hold NOTHING typed, and people
 * reached for the scrim, got nothing, and reported the modal as one they could
 * not get out of. Worse, the click moved focus to <body>, and Escape is handled
 * on this component's own wrapper, so after one press on the scrim Escape was
 * dead too and the × was the only exit — at the top of a panel that had often
 * scrolled it out of view.
 *
 * So, three changes, and the first is the ruling:
 *
 *   1. A press that STARTS and ENDS on the scrim closes the dialog — unless the
 *      person has typed into a text field inside it since it opened. Then the
 *      dialog stays and the × pulses, so the refusal is visible rather than a
 *      dead click. "Typed" is an `input` event from a text-like field, never a
 *      select or a checkbox: choosing a variant is not work you can lose.
 *      Starts AND ends, because a drag that selects text in the panel and is
 *      released over the scrim fires `click` on the scrim — the common
 *      ancestor — and must not close anything.
 *   2. The scrim's pointerdown is `preventDefault`ed, so focus never leaves the
 *      panel, and a document-level Escape covers the case where it has anyway.
 *   3. The header is sticky (hub.css), so the × is on screen however far the
 *      panel has scrolled.
 *
 * DO NOT PUT THE NEVER-DISMISS RULE BACK on the strength of the old comment.
 * The draft it protected is still protected — by the dirty check, which is the
 * narrower instrument. Escape still always closes: it is a deliberate key.
 */
export function Dialog({
  open,
  title,
  onClose,
  children,
  className = '',
  variant = 'modal',
}: {
  open: boolean
  /** Plain text: it names the dialog to a screen reader and labels the × */
  title: string
  onClose: () => void
  children: React.ReactNode
  className?: string
  /**
   * 'tray' (REC-65): the same dialog — trap, Escape, focus return, scroll lock,
   * the typed-scrim nudge — laid out as a right-hand sheet over a list that
   * stays in view. A layout, not a second component, so the tray cannot drift
   * from what this one learned the hard way. Full screen at phone width.
   *
   * 'sheet' (HLP-49): a bottom sheet ~90% high, the page visible above it -
   * the guide sheet below the width where it docks. Same trap and Escape.
   */
  variant?: 'modal' | 'tray' | 'sheet'
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)
  const titleId = useId()
  const isTopmost = useSurfaceLayer(open)
  // Held in refs: none of these may re-render the dialog, and the document
  // listener below must see the latest onClose without re-registering.
  const typed = useRef(false)
  const pressedScrim = useRef(false)
  const latestClose = useRef(onClose)
  latestClose.current = onClose
  const [nudge, setNudge] = useState(0)

  useEffect(() => {
    if (!open) return
    typed.current = false

    // Remembered BEFORE focus moves. On the way out it goes back here, which is
    // the half of a modal people notice only when it is missing: closing one and
    // landing at the top of the document means finding your place again.
    returnTo.current = document.activeElement as HTMLElement | null

    // The PANEL takes focus, not the first control in it. Focusing the first
    // control skips the title, so a screen reader opens on a text field with no
    // idea what it belongs to; focusing the × makes "Close" the first thing
    // anybody hears about a form they just chose to open.
    panelRef.current?.focus()

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Escape when focus has left the panel (a press somewhere this component
    // did not prevent). The wrapper's onKeyDown only hears keys from inside,
    // so without this Escape silently stops working. Inside-focus keys are
    // still handled — and stopped — by onKeyDown, so this never fires twice.
    function onDocKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      if (!isTopmost()) return
      if (panelRef.current?.contains(document.activeElement)) return
      latestClose.current()
    }
    document.addEventListener('keydown', onDocKeyDown)

    return () => {
      document.removeEventListener('keydown', onDocKeyDown)
      document.body.style.overflow = previousOverflow
      // Guarded: the trigger can legitimately be gone by now — a button that
      // unmounted with the thing it acted on.
      if (returnTo.current?.isConnected) returnTo.current.focus()
    }
  }, [open, isTopmost])

  // Clears the pulse so the next refused press can replay it.
  useEffect(() => {
    if (nudge === 0) return
    const t = window.setTimeout(() => setNudge(0), 700)
    return () => window.clearTimeout(t)
  }, [nudge])

  function onInput(e: React.FormEvent) {
    if (isTextEntry(e.target)) typed.current = true
  }

  function onScrimPointerDown(e: React.PointerEvent) {
    // Only a press on the scrim itself, and only while this dialog is the top
    // surface: with a picker open inside it, the press is the picker's to
    // close, and closing the whole form under it would be the Escape bug again.
    pressedScrim.current = e.target === e.currentTarget && isTopmost()
    // Keep focus in the panel, so Escape and the Tab trap still work after it.
    if (e.target === e.currentTarget) e.preventDefault()
  }

  function onScrimClick(e: React.MouseEvent) {
    const armed = pressedScrim.current
    pressedScrim.current = false
    if (!armed || e.target !== e.currentTarget) return
    if (typed.current) {
      setNudge((n) => n + 1)
      return
    }
    onClose()
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      // A picker open INSIDE this dialog is above it on the stack and owns the
      // key. Without this, Escape aimed at a dropdown closes the whole form.
      if (!isTopmost()) return
      e.stopPropagation()
      onClose()
      return
    }
    if (e.key !== 'Tab') return
    // 161: a dialog opened over another one (the tag editor over the composer,
    // the opt-out over the composer) bubbles its Tab through this handler too,
    // through the React tree. Only the top dialog traps.
    if (!isTopmost()) return

    const items = focusableWithin(panelRef.current)
    if (items.length === 0) {
      // Nothing to move to, so Tab must not leave. A dialog you can tab out of
      // is a dialog the page behind is still reachable through.
      e.preventDefault()
      return
    }

    const active = document.activeElement
    const index = items.indexOf(active as HTMLElement)
    if (e.shiftKey && index <= 0) {
      // index === -1 is the panel itself, which is where focus starts: shift-Tab
      // from there should wrap to the end rather than escape upwards.
      e.preventDefault()
      items[items.length - 1].focus()
    } else if (!e.shiftKey && index === items.length - 1) {
      e.preventDefault()
      items[0].focus()
    }
  }

  if (!open) return null

  const node = (
    <div
      className={variant === 'modal' ? 'dialog' : `dialog dialog--${variant}`}
      role="presentation"
      onKeyDown={onKeyDown}
      onPointerDown={onScrimPointerDown}
      onClick={onScrimClick}
    >
      <div
        ref={panelRef}
        className={`dialog__panel ${className}`.trim()}
        onInput={onInput}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="dialog__head">
          <h2 className="dialog__title" id={titleId}>{title}</h2>
          {/* × is one of the four glyphs the design system sanctions (dismiss,
              download, search) and this is a dismiss. Removal that acts on a
              RECORD stays worded — "Remove", "Take down" — because the cost of
              misreading those is not a reopened panel. */}
          <button
            type="button"
            className={nudge ? 'dialog__x dialog__x--nudge' : 'dialog__x'}
            aria-label={`Close ${title}`}
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="dialog__body">{children}</div>
      </div>
    </div>
  )
  // In a browser, to <body> — see "A PORTAL IN THE BROWSER" above. Under
  // renderToString there is no document, and in place is the only option.
  return typeof document === 'undefined' ? node : createPortal(node, document.body)
}

/**
 * Everything inside the panel that can take focus, in document order.
 *
 * `:not([disabled])` matters more than it looks: the give form disables its
 * submit button until the form is ready, and a trap that wrapped onto a
 * disabled control would put focus nowhere and look like the keyboard had
 * stopped working.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function focusableWithin(root: HTMLElement | null): HTMLElement[] {
  if (!root) return []
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    // offsetParent is null for anything display:none — a collapsed section's
    // controls are in the DOM and must not be in the tab ring.
    (el) => el.offsetParent !== null || el === document.activeElement,
  )
}

/**
 * A field whose contents are work somebody could lose. Selects, checkboxes,
 * radios, ranges and file inputs are choices, not prose, and re-making one
 * costs a click — so they do not hold the dialog open.
 */
const TEXT_TYPES = new Set(['text', 'search', 'email', 'tel', 'url', 'number', 'password', ''])
function isTextEntry(target: EventTarget | null): boolean {
  if (target instanceof HTMLTextAreaElement) return true
  if (target instanceof HTMLInputElement) return TEXT_TYPES.has(target.type)
  return target instanceof HTMLElement && target.isContentEditable
}
